import { ComponentProps, FunctionComponent, memo, useCallback, useEffect, useState } from 'react';

import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { TagModel } from 'models';

import { FieldContainer, GridColumn, createStyledRhfForm } from 'core/forms';
import { useEvent } from 'core/hooks';
import { Button, DropdownField, Icon, InputField, Label, SwitchField } from 'core/ui';

import { TagsEditService } from '../services';
import { EditFormValues } from '../types';

const NullParentTagOption = { id: null, name: '' };

type TagsFieldTemplateFormProps = {
  tag: TagModel;
  parentTags: TagModel[];
  onSubmit: (values: EditFormValues) => Promise<void>;
  isSubmitting: boolean;
};

export const TagsFieldTemplateForm: FunctionComponent<TagsFieldTemplateFormProps> = memo(({ tag, parentTags, onSubmit, isSubmitting }) => {
  const [localTag, setLocalTag] = useState<TagModel>(tag);

  const rhfContext = useForm<EditFormValues>({
    defaultValues: { ...TagsEditService.EditFormDefaults },
    mode: 'onSubmit',
  });
  const {
    reset,
    formState: { errors },
  } = rhfContext;

  // Initialize the form
  const { control } = useForm({
    defaultValues: {
      children: localTag?.children ?? [],
    },
  });

  // Manage the field array
  const { remove, replace } = useFieldArray({
    control,
    name: 'children',
  });

  // handler for useFieldArray Append event
  const handleApppend = useEvent(() => {
    if (localTag?.children) {
      localTag.children.push({
        id: 0,
        name: '',
        description: '',
        tagFieldOption: null,
        active: true,
        children: [],
        parentTagId: null,
        required: false,
        multipleSelection: false,
      });
      replace(localTag.children);
      setLocalTag({ ...localTag });
    } else {
      console.error('Cannot append, tag or tag.children is not initialized');
    }
  });

  // handler for useFieldArray Remove event
  const handleRemove = (index: number) => {
    if (localTag?.children) {
      localTag.children.splice(index, 1);
      remove(index);
      setLocalTag({ ...localTag });
    } else {
      console.error('Cannot remove, tag or tag.children is not initialized');
    }
  };

  useEffect(() => {
    setLocalTag(tag);
    replace(tag?.children ?? []);

    // Ensure the tag data is properly initialized for the form
    const formValues = TagsEditService.copyModelToForm(tag, parentTags);

    // Set defaults for any missing values
    const defaultedValues: EditFormValues = {
      ...TagsEditService.EditFormDefaults,
      ...formValues,
      name: formValues.name || '',
      description: formValues.description || null,
      parentTag: formValues.parentTag || null,
      active: typeof formValues.active === 'boolean' ? formValues.active : true,
      children: formValues.children || [],
    };

    reset(defaultedValues, {
      keepValues: false,
      keepDefaultValues: false,
    });
  }, [tag, parentTags, replace, reset]);

  const handleSubmit: SubmitHandler<EditFormValues> = useCallback(
    async (values, event) => {
      event?.preventDefault();

      // Validate name field
      if (!values.name || values.name.trim() === '') {
        return;
      }

      // Make sure children array is always defined
      const validatedValues = {
        ...values,
        children: values.children || [],
      };

      await onSubmit(validatedValues);
    },
    [onSubmit],
  );

  return (
    <FormProvider {...rhfContext}>
      <StyledForm
        id="tag-form"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck="false"
        noValidate
        onSubmit={rhfContext.handleSubmit(handleSubmit)}
      >
        <GridColumn columnStart="1" isLabelColumn>
          <Label>Tag Name</Label>
        </GridColumn>
        <GridColumn columnStart="2" columnEnd="span 1">
          <FieldContainer $hideLabel>
            <StyledInput name="name" placeholder="Tag Name" required validationMessage={errors.name ? 'Tag name is required' : undefined} />
          </FieldContainer>
        </GridColumn>

        <GridColumn columnStart="1" isLabelColumn>
          <Label>Description</Label>
        </GridColumn>
        <GridColumn columnStart="2" columnEnd="span 1">
          <FieldContainer $hideLabel>
            <StyledInput name="description" placeholder="Description" />
          </FieldContainer>
        </GridColumn>

        <GridColumn columnStart="1" isLabelColumn>
          <Label>Parent Tag Name</Label>
        </GridColumn>
        <GridColumn columnStart="2" columnEnd="span 1">
          <FieldContainer $hideLabel>
            <DropdownField
              name="parentTag"
              data={parentTags}
              textField="name"
              valueField="id"
              dataItemKey="id"
              defaultItem={NullParentTagOption}
              style={{ marginBottom: '10px' }}
            />
          </FieldContainer>
        </GridColumn>

        <GridColumn columnStart="1" isLabelColumn>
          <Label>Add options below to display</Label>
        </GridColumn>
        <GridColumn columnStart="2" columnEnd="span 3">
          <FieldContainer>
            {(localTag?.children ?? []).map((field, index) => (
              <div key={field.id}>
                <StyledLineItemContainer>
                  <StyledLine>
                    <InputField name={`children.${index}.name`} key={`children.${index}.id`} defaultValue={field.name || ''} />
                    <StyledCloseButton>
                      <Button type="button" onClick={() => handleRemove(index)}>
                        <Icon icon={faCircleXmark} />
                      </Button>
                    </StyledCloseButton>
                  </StyledLine>
                </StyledLineItemContainer>
                <span style={{ marginLeft: '10px' }} />
              </div>
            ))}
            <StyledButtonContainer>
              <Button type="button" onClick={handleApppend}>
                Add Option / Sub-Tag
              </Button>
            </StyledButtonContainer>

            <span style={{ marginLeft: '10px' }} />
          </FieldContainer>
        </GridColumn>

        <GridColumn columnStart="1" isLabelColumn>
          <Label>Active</Label>
        </GridColumn>
        <GridColumn columnStart="2" columnEnd="span 1">
          <FieldContainer $hideLabel>
            <SwitchField name="active" />
          </FieldContainer>
        </GridColumn>
      </StyledForm>
    </FormProvider>
  );
});

TagsFieldTemplateForm.displayName = 'TagsFieldTemplateForm';

const StyledForm = createStyledRhfForm('min-content 400px');

const StyledLine = styled.div`
  display: flex;
`;

const StyledCloseButton = styled.button`
  all: unset;
  color: ${({ theme }) => theme.colors.primary};
  padding: 0 7px;
`;

const StyledLineItemContainer = styled.div`
  display: grid;
  row-gap: 6px;
  width: 50%;
`;

const StyledButtonContainer = styled.div`
  padding: 6px 0 0 0;
`;

const StyledInput = styled(InputField)`
  margin-bottom: 10px;
`;
